import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    fetchNetworkTableAreas,
    fetchNetworkTableDmas,
    fetchNetworkTablePois,
    type AreaSelector,
    type DmaSelector,
    type NetworkTableAreaRow,
    type NetworkTableDmaRow,
    type NetworkTablePoiRow,
    type PagedResponse,
} from '@/api/networkTableApi';
import { serialiseEntityFiltersToParams } from '@/components/map/utils/entityMapFilters';
import type { EntityMapFilterState } from '@/components/map/utils/entityMapFilters';
import { showErrorToast } from '@/errors/showErrorToast';
import { stableKey } from '@/table-view/stableKey';

/**
 * The root Area level is NOT paged: the largest client has 8 Areas, so the page control never
 * rendered, and returning the level whole lets the API skip its metadata query entirely. This
 * constant only feeds the request key, whose shape is dictated by the paged levels beneath.
 */
const ROOT_PAGE = 1;

/** Children load 10 at a time behind an explicit "N more…" action. */
export const CHILD_PAGE_SIZE = 10;

/**
 * The table has one shared header row, so a sort is chosen by COLUMN and applied at whichever
 * levels it is meaningful for — "one active sort applied where meaningful".
 */
export type NetworkTableSortColumn = 'name' | 'reference' | 'dmaCount' | 'poiCount' | 'leaksFound' | 'potentialSaving';

export interface NetworkTableSort {
    column: NetworkTableSortColumn;
    direction: 'asc' | 'desc';
}

/**
 * Column → the backend sort field for each level, or null where the column has no meaning there
 * (the level then keeps its backend default ordering). Every value below is a key from the
 * matching whitelist in NetworkTableService (AreaSortColumns / DmaSortColumns / PoiSortColumns) —
 * an unlisted value would be a 400.
 */
const AREA_SORT_FIELD: Record<NetworkTableSortColumn, string | null> = {
    name: 'name',
    reference: null, // an Area has no reference — its name is its identity
    dmaCount: 'dmaCount',
    poiCount: 'poiCount',
    leaksFound: 'leaksFound',
    potentialSaving: 'potentialSaving',
};

const DMA_SORT_FIELD: Record<NetworkTableSortColumn, string | null> = {
    // Each column sorts by what it DISPLAYS: the name column shows the DMA's name, and its ident
    // moved to the Reference column. Sorting the name column by ident would order the rows by a
    // value no longer in them.
    name: 'name',
    reference: 'ident',
    dmaCount: null, // a DMA has no DMA count
    poiCount: 'poiCount',
    leaksFound: 'leaksFound',
    potentialSaving: 'potentialSaving',
};

const POI_SORT_FIELD: Record<NetworkTableSortColumn, string | null> = {
    // The worklist leads the POI row's displayed identity ("Worklist X · Stop Y"), and it is text —
    // the order "A to Z" promises. The backend's tie-breaks keep stops numerically ascending within
    // each worklist, then id. (Stop alone would sort a numeric value under alphabetical wording,
    // by the SECOND fact of the identity, interleaving worklists.)
    name: 'worklist',
    // A POI has no reference — confirmed, not merely unimplemented. Its Worklist and Stop identify
    // it on its own row, and POI rows span the table rather than filling the Reference cell.
    reference: null,
    dmaCount: null,
    poiCount: null,
    // Deliberately null: leakage_result is a categorical enum whose stored order is arbitrary, so
    // neither "Lowest to Highest" nor "Highest to Lowest" describes it. POI rows keep their default
    // order while the Area and DMA levels sort by their aggregate leak counts.
    leaksFound: null,
    // Deliberately null: there is no per-POI Potential Saving concept anywhere in the platform.
    potentialSaving: null,
};

/** Sort params for one level, or `{}` where the active column has no meaning there. */
function sortParams(sort: NetworkTableSort | null, fields: Record<NetworkTableSortColumn, string | null>) {
    if (sort === null) return {};
    const sortBy = fields[sort.column];
    return sortBy === null ? {} : { sortBy, sortDir: sort.direction };
}

/**
 * One lazily-loaded set of children beneath a parent.
 *
 * Server pagination (`nextPage`/`hasNext`/`serverItemsConsumed`) is tracked separately
 * from the deduplicated `rows`: if the server ever repeats a record on its final page,
 * de-duplication shrinks `rows` but must never resurrect the "load more" action —
 * `hasNext` alone decides whether more pages exist.
 */
interface ChildNode<TRow> {
    /** Accumulated across pages, de-duplicated, in server order. Display only. */
    rows: TRow[];
    /** Latest server TotalCount. May shrink between pages as data changes. */
    total: number;
    /** Raw items received across pages, before de-duplication. Drives the action label. */
    serverItemsConsumed: number;
    /** Page to request next. Advances only on success, so a retry repeats the failed page. */
    nextPage: number;
    /** Authoritative "more pages exist" flag, straight from the server. */
    hasNext: boolean;
    status: 'loading' | 'loaded' | 'error';
}

/** A flattened, render-ready row for the hierarchical table. */
export type NetworkTableRow =
    | { kind: 'area'; key: string; area: NetworkTableAreaRow; expanded: boolean }
    | { kind: 'dma'; key: string; area: NetworkTableAreaRow; dma: NetworkTableDmaRow; expanded: boolean }
    // POI rows carry their parents: the converter needs the hierarchy context, and Locate/Copy/
    // Show Details all build from them.
    | { kind: 'poi'; key: string; poi: NetworkTablePoiRow; area: NetworkTableAreaRow; dma: NetworkTableDmaRow }
    | {
          kind: 'status';
          key: string;
          level: 'dma' | 'poi';
          state: 'loading' | 'error' | 'empty';
          // Context for the panel to retry a failed load (built in an event handler, not render).
          area: NetworkTableAreaRow;
          dma?: NetworkTableDmaRow;
      }
    | {
          kind: 'loadMore';
          key: string;
          level: 'dma' | 'poi';
          /** Size of the next batch — "N more DMAs in Alpha". */
          nextCount: number;
          parentLabel: string;
          state: 'idle' | 'loading' | 'error';
          area: NetworkTableAreaRow;
          dma?: NetworkTableDmaRow;
      };

/** Identifies a lazily-loaded child collection within a query context. */
type ChildResource = { kind: 'dmas'; area: AreaSelector } | { kind: 'pois'; area: AreaSelector; dma: DmaSelector };

/** Any addressable collection: the root Area list, or a child collection. */
type TableResource = { kind: 'areas' } | ChildResource;

function areaSelectorOf(area: NetworkTableAreaRow): AreaSelector {
    return area.isUnassigned ? { type: 'unassigned' } : { type: 'assigned', name: area.name ?? '' };
}

function dmaSelectorOf(dma: NetworkTableDmaRow): DmaSelector {
    return dma.isUnassigned ? { type: 'unassigned' } : { type: 'assigned', ident: dma.ident ?? '' };
}

export function areaLabel(area: NetworkTableAreaRow): string {
    return area.isUnassigned ? 'Unassigned' : (area.name ?? '');
}

export function dmaLabel(dma: NetworkTableDmaRow): string {
    return dma.isUnassigned ? 'Unassigned' : (dma.ident ?? '');
}

/** Accumulated-state key for a parent: the whole query context plus the resource. */
function parentKeyOf(ctxKey: string, resource: TableResource): string {
    return stableKey({ ctxKey, resource });
}

/** One request's key: the parent key plus the requested page (and retry attempt, where used). */
function requestKeyOf(ctxKey: string, resource: TableResource, page: number, attempt = 0): string {
    return stableKey({ ctxKey, resource, page, attempt });
}

function dmaIdentity(dma: NetworkTableDmaRow): string {
    return stableKey(dmaSelectorOf(dma));
}

function poiIdentity(poi: NetworkTablePoiRow): string {
    return poi.id;
}

function emptyNode<TRow>(): ChildNode<TRow> {
    return { rows: [], total: 0, serverItemsConsumed: 0, nextPage: 1, hasNext: false, status: 'loading' };
}

function withStatus<TRow>(node: ChildNode<TRow> | undefined, status: 'loading' | 'error'): ChildNode<TRow> {
    return node ? { ...node, status } : { ...emptyNode<TRow>(), status };
}

/** Appends a page, de-duplicating rows while tracking server pagination independently. */
function appendPage<TRow>(
    node: ChildNode<TRow> | undefined,
    response: PagedResponse<TRow>,
    requestedPage: number,
    identity: (row: TRow) => string,
): ChildNode<TRow> {
    const existing = node?.rows ?? [];
    const seen = new Set(existing.map(identity));
    const fresh = response.items.filter((row) => {
        const key = identity(row);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });

    return {
        rows: [...existing, ...fresh],
        total: response.totalCount,
        serverItemsConsumed: (node?.serverItemsConsumed ?? 0) + response.items.length,
        // Trust the requested page rather than the echoed one, so a mis-echo cannot stall or skip.
        nextPage: requestedPage + 1,
        hasNext: response.hasNext,
        status: 'loaded',
    };
}

/** Size of the next batch. Falls back to a full page if totals shrank below what we consumed. */
function nextBatchCount<TRow>(node: ChildNode<TRow>): number {
    const remaining = node.total - node.serverItemsConsumed;
    return remaining > 0 ? Math.min(CHILD_PAGE_SIZE, remaining) : CHILD_PAGE_SIZE;
}

function loadMoreState<TRow>(node: ChildNode<TRow>): 'idle' | 'loading' | 'error' {
    if (node.status === 'loading') return 'loading';
    if (node.status === 'error') return 'error';
    return 'idle';
}

/**
 * All child state, stamped with the query context it was built under. A context change
 * (client, filters, search, sort) invalidates it by derivation at render — no reset effect,
 * which would trip `react-hooks/set-state-in-effect` — and any in-flight response whose
 * stamp no longer matches is dropped rather than appended into the new hierarchy.
 */
interface ChildrenState {
    ctxKey: string;
    dmaNodes: Map<string, ChildNode<NetworkTableDmaRow>>;
    poiNodes: Map<string, ChildNode<NetworkTablePoiRow>>;
    /** Parent keys of the expanded `dmas` resources. */
    expandedAreas: Set<string>;
    /** Parent keys of the expanded `pois` resources. */
    expandedDmas: Set<string>;
}

function emptyChildren(ctxKey: string): ChildrenState {
    return {
        ctxKey,
        dmaNodes: new Map(),
        poiNodes: new Map(),
        expandedAreas: new Set(),
        expandedDmas: new Set(),
    };
}

interface RootData {
    areas: NetworkTableAreaRow[];
    totalFilteredPoiCount: number;
}

/**
 * The one-shot latch for search auto-expansion (§ "conditional search auto-expansion").
 *
 * Existence means the expansion has already been ATTEMPTED for `ctxKey` — never that it is still
 * wanted. That is what makes a manual collapse stick: the branch is opened once, imperatively,
 * from the response handlers, and nothing ever re-derives it. Released when the context changes,
 * so each committed search gets exactly one attempt.
 */
interface AutoExpandState {
    ctxKey: string;
    /** Parent key of the auto-expanded Area's `dmas` resource — the DMA step matches on this. */
    areaParentKey: string;
    /** True once the DMA step has run, so a later DMA page cannot expand a second branch. */
    dmaDone: boolean;
}

interface UseNetworkTableParams {
    clientId: string | null;
    /** When false, no requests are made (the table is closed). */
    enabled: boolean;
    /**
     * The map's COMMITTED filter state, or null while filters are inactive. Never the draft:
     * requests must change only on Apply/Reset. The map owns this state — the table has none.
     */
    filters?: EntityMapFilterState | null;
    /**
     * The COMMITTED (debounced) search term — the table owns the raw input, this is what queries use.
     * Empty string means no search.
     */
    search?: string;
}

/**
 * Owns the Network Overview table's server data: a paginated root of Area rows, plus
 * lazily-loaded DMA and POI children paged 10 at a time behind an explicit action.
 * No requests are made while `enabled` is false.
 *
 * The root level is returned whole; only the children are paged, appending beneath their parent
 * and surviving collapse/reopen.
 *
 * Root loading is derived from a request-key tracker rather than a synchronous setState in
 * the fetch effect, matching the portal's other data hooks.
 */
export function useNetworkTable({ clientId, enabled, filters = null, search = '' }: UseNetworkTableParams) {
    // The sort control lives inside the table, so the hook owns it — like `page`, it is query state.
    // It deliberately survives a filter/search change; only the results are invalidated.
    const [sort, setSort] = useState<NetworkTableSort | null>(null);
    // Bumped by retryRoot to force a refetch of an identical request.
    const [reloadToken, setReloadToken] = useState(0);

    const ctxKey = useMemo(
        () => stableKey({ clientId, filters, search, sort, childPageSize: CHILD_PAGE_SIZE }),
        [clientId, filters, search, sort],
    );

    // The committed filters as query params. Changing them changes ctxKey, which resets pagination
    // and expansion, drops in-flight responses and refetches the root — see the guards below.
    const filterParams = useMemo(() => (filters ? serialiseEntityFiltersToParams(filters) : {}), [filters]);

    const [data, setData] = useState<RootData | null>(null);
    const [loadedKey, setLoadedKey] = useState<string | null>(null);
    const [errorKey, setErrorKey] = useState<string | null>(null);
    const [childrenState, setChildrenState] = useState<ChildrenState>(() => emptyChildren(ctxKey));

    // Child state belonging to a superseded context is ignored, not migrated.
    const children = useMemo(
        () => (childrenState.ctxKey === ctxKey ? childrenState : emptyChildren(ctxKey)),
        [childrenState, ctxKey],
    );

    // Guards against duplicate requests (repeated clicks) and stale appends.
    const inFlightRef = useRef<Set<string>>(new Set());
    const controllersRef = useRef<Map<string, AbortController>>(new Map());

    /**
     * Tracks the one-shot search auto-expansion for the current query context. Non-null means it
     * has already been attempted for that context, so a manual collapse is never undone — the
     * expansion is applied once, imperatively, rather than derived every render.
     */
    const autoExpandRef = useRef<AutoExpandState | null>(null);

    /**
     * The child loaders, refreshed each render. The fetch handlers below call them to chain the
     * auto-expansion WITHOUT taking them as effect dependencies — their identity changes with the
     * node state, which would otherwise re-run the root fetch on every child load.
     */
    const loadersRef = useRef<{
        loadDmaPage: (area: NetworkTableAreaRow) => void;
        loadPoiPage: (area: NetworkTableAreaRow, dma: NetworkTableDmaRow) => void;
    }>({ loadDmaPage: () => undefined, loadPoiPage: () => undefined });

    // Cancel every in-flight child request when the query context changes (or on unmount), and
    // release the auto-expansion latch so the next committed search gets its own single attempt.
    // Cleanup only aborts and clears refs — it never sets state.
    useEffect(() => {
        const controllers = controllersRef.current;
        const inFlight = inFlightRef.current;
        const autoExpand = autoExpandRef;
        return () => {
            controllers.forEach((controller) => {
                controller.abort();
            });
            controllers.clear();
            inFlight.clear();
            autoExpand.current = null;
        };
    }, [ctxKey]);

    // `attempt` makes a retry of an identical request a distinct key, so status re-derives to loading.
    const rootKey = enabled && clientId ? requestKeyOf(ctxKey, { kind: 'areas' }, ROOT_PAGE, reloadToken) : null;

    // Fetch the root Area page. Only commits on completion — loading is derived (below).
    useEffect(() => {
        if (!enabled || !clientId) return;

        const controller = new AbortController();
        const key = requestKeyOf(ctxKey, { kind: 'areas' }, ROOT_PAGE, reloadToken);

        fetchNetworkTableAreas(
            clientId,
            {
                filters: filterParams,
                search: search === '' ? undefined : search,
                ...sortParams(sort, AREA_SORT_FIELD),
            },
            controller.signal,
        )
            .then((response) => {
                // Guard the COMMIT, not just the auto-expansion below it. This check used to sit
                // after the two setStates, so a superseded response still wrote its rows and its
                // key — harmless only because `activeData` independently withholds anything whose
                // key is not the current one. That made the abort guard look load-bearing when the
                // key check was doing all the work, and would have exposed stale rows the moment
                // anyone relaxed it. Matches the .catch below and both child loaders.
                if (controller.signal.aborted) return;

                setData({
                    areas: response.items,
                    totalFilteredPoiCount: response.totalFilteredPoiCount,
                });
                setLoadedKey(key);
                // Release any error recorded against THIS key. Both keys are compared against
                // the current request key, so a failure that is never cleared outlives its
                // request: returning to a context that failed earlier (reopening the table,
                // retyping a search term, re-applying the same filters) reproduces the key and
                // `status` reports 'error' again — and, because `errorKey` is tested first,
                // keeps reporting it after the refetch succeeds, painting the error state over
                // freshly loaded rows. `retryRoot` bumps `reloadToken` into a NEW key, which is
                // why the retry path never exposed this.
                setErrorKey(null);

                // --- Search auto-expansion, step 1 of 2: open the single matching Area. ---
                // A search precise enough to identify one Network Record (a what3words, a stop, an
                // identifier) should reveal it without three more clicks. Chained from the response
                // rather than an effect: setState here is legitimate, whereas an effect body would
                // trip `react-hooks/set-state-in-effect`.
                //
                // Filters alone never trigger this — only a committed search that isolates one POI.
                if (search === '' || response.totalFilteredPoiCount !== 1) return;
                // One matching POI resolves to exactly one (Area, DMA), so the root page holds one
                // row. Anything else means the invariant broke; expanding would be a guess.
                if (response.items.length !== 1) return;
                // Already attempted for this context — never reopen what the user collapsed.
                if (autoExpandRef.current !== null) return;

                const area = response.items[0];
                const areaParentKey = parentKeyOf(ctxKey, { kind: 'dmas', area: areaSelectorOf(area) });
                autoExpandRef.current = { ctxKey, areaParentKey, dmaDone: false };

                setChildrenState((prev) => {
                    const base = prev.ctxKey === ctxKey ? prev : emptyChildren(ctxKey);
                    return { ...base, expandedAreas: new Set(base.expandedAreas).add(areaParentKey) };
                });
                // Goes through the normal paged loader — no manufactured rows, and the same
                // in-flight, abort and context-stamp protections as a user-driven expand.
                loadersRef.current.loadDmaPage(area);
            })
            .catch((error: unknown) => {
                if (controller.signal.aborted) return;
                setErrorKey(key);
                showErrorToast(error);
            });

        return () => {
            controller.abort();
        };
    }, [clientId, ctxKey, enabled, filterParams, reloadToken, search, sort]);

    /**
     * Root rows are shown only while they belong to the CURRENT request key. `data` still holds the
     * previous response until the new one lands, so without this the old hierarchy would flash under
     * a new filter/search/sort (or page) before being replaced.
     */
    const activeData = loadedKey === rootKey ? data : null;

    /** Re-runs the current root request after a failure. */
    const retryRoot = useCallback(() => {
        setReloadToken((token) => token + 1);
    }, []);

    /**
     * Applies an absolute sort selection from the header menu — a column + direction, or null to
     * clear back to the backend's default ordering. Any CHANGE to the sort changes ctxKey, so
     * pagination, expansion and cached children all reset — rows from two sort contexts can never
     * be interleaved.
     *
     * Re-selecting the already-active value keeps the CURRENT state reference so React bails out:
     * no refetch, no reset, no collapse. Bailing on the reference matters — the root fetch effect
     * depends on `sort` itself, so an equal-but-new object would still re-run it.
     */
    const applySort = useCallback((next: NetworkTableSort | null) => {
        setSort((current) =>
            current?.column === next?.column && current?.direction === next?.direction ? current : next,
        );
    }, []);

    /**
     * Requests the next unloaded page for a parent — the same call serves the first load,
     * "load more" and retry, because `nextPage` only advances on success.
     */
    const loadDmaPage = useCallback(
        (area: NetworkTableAreaRow) => {
            if (!clientId) return;

            const resource: ChildResource = { kind: 'dmas', area: areaSelectorOf(area) };
            const parentKey = parentKeyOf(ctxKey, resource);
            const requestedPage = children.dmaNodes.get(parentKey)?.nextPage ?? 1;
            const requestKey = requestKeyOf(ctxKey, resource, requestedPage);

            const inFlight = inFlightRef.current;
            if (inFlight.has(requestKey)) return;
            inFlight.add(requestKey);

            const controller = new AbortController();
            controllersRef.current.set(requestKey, controller);

            setChildrenState((prev) => {
                const base = prev.ctxKey === ctxKey ? prev : emptyChildren(ctxKey);
                const dmaNodes = new Map(base.dmaNodes).set(
                    parentKey,
                    withStatus(base.dmaNodes.get(parentKey), 'loading'),
                );
                return { ...base, dmaNodes };
            });

            fetchNetworkTableDmas(
                clientId,
                areaSelectorOf(area),
                {
                    page: requestedPage,
                    pageSize: CHILD_PAGE_SIZE,
                    filters: filterParams,
                    search: search === '' ? undefined : search,
                    ...sortParams(sort, DMA_SORT_FIELD),
                },
                controller.signal,
            )
                .then((response) => {
                    if (controller.signal.aborted) return;
                    setChildrenState((prev) => {
                        // Superseded context — drop rather than append into the new hierarchy.
                        if (prev.ctxKey !== ctxKey) return prev;
                        const node = appendPage(prev.dmaNodes.get(parentKey), response, requestedPage, dmaIdentity);
                        return { ...prev, dmaNodes: new Map(prev.dmaNodes).set(parentKey, node) };
                    });

                    // --- Search auto-expansion, step 2 of 2: open the single matching DMA. ---
                    const auto = autoExpandRef.current;
                    // Every clause is a stale guard: `ctxKey` rejects a response from a superseded
                    // search, `areaParentKey` rejects a DMA page the user opened by hand elsewhere,
                    // and `dmaDone` keeps the chain to a single step.
                    if (auto === null || auto.ctxKey !== ctxKey || auto.dmaDone) return;
                    if (auto.areaParentKey !== parentKey) return;
                    if (response.items.length !== 1) return;

                    autoExpandRef.current = { ...auto, dmaDone: true };

                    const dma = response.items[0];
                    const dmaParentKey = parentKeyOf(ctxKey, {
                        kind: 'pois',
                        area: areaSelectorOf(area),
                        dma: dmaSelectorOf(dma),
                    });
                    setChildrenState((prev) => {
                        if (prev.ctxKey !== ctxKey) return prev;
                        return { ...prev, expandedDmas: new Set(prev.expandedDmas).add(dmaParentKey) };
                    });
                    loadersRef.current.loadPoiPage(area, dma);
                })
                .catch((error: unknown) => {
                    if (controller.signal.aborted) return;
                    setChildrenState((prev) => {
                        if (prev.ctxKey !== ctxKey) return prev;
                        const node = withStatus(prev.dmaNodes.get(parentKey), 'error');
                        return { ...prev, dmaNodes: new Map(prev.dmaNodes).set(parentKey, node) };
                    });
                    showErrorToast(error);
                })
                .finally(() => {
                    inFlightRef.current.delete(requestKey);
                    controllersRef.current.delete(requestKey);
                });
        },
        [children.dmaNodes, clientId, ctxKey, filterParams, search, sort],
    );

    const loadPoiPage = useCallback(
        (area: NetworkTableAreaRow, dma: NetworkTableDmaRow) => {
            if (!clientId) return;

            const resource: ChildResource = { kind: 'pois', area: areaSelectorOf(area), dma: dmaSelectorOf(dma) };
            const parentKey = parentKeyOf(ctxKey, resource);
            const requestedPage = children.poiNodes.get(parentKey)?.nextPage ?? 1;
            const requestKey = requestKeyOf(ctxKey, resource, requestedPage);

            const inFlight = inFlightRef.current;
            if (inFlight.has(requestKey)) return;
            inFlight.add(requestKey);

            const controller = new AbortController();
            controllersRef.current.set(requestKey, controller);

            setChildrenState((prev) => {
                const base = prev.ctxKey === ctxKey ? prev : emptyChildren(ctxKey);
                const poiNodes = new Map(base.poiNodes).set(
                    parentKey,
                    withStatus(base.poiNodes.get(parentKey), 'loading'),
                );
                return { ...base, poiNodes };
            });

            fetchNetworkTablePois(
                clientId,
                areaSelectorOf(area),
                dmaSelectorOf(dma),
                {
                    page: requestedPage,
                    pageSize: CHILD_PAGE_SIZE,
                    filters: filterParams,
                    search: search === '' ? undefined : search,
                    ...sortParams(sort, POI_SORT_FIELD),
                },
                controller.signal,
            )
                .then((response) => {
                    if (controller.signal.aborted) return;
                    setChildrenState((prev) => {
                        if (prev.ctxKey !== ctxKey) return prev;
                        const node = appendPage(prev.poiNodes.get(parentKey), response, requestedPage, poiIdentity);
                        return { ...prev, poiNodes: new Map(prev.poiNodes).set(parentKey, node) };
                    });
                })
                .catch((error: unknown) => {
                    if (controller.signal.aborted) return;
                    setChildrenState((prev) => {
                        if (prev.ctxKey !== ctxKey) return prev;
                        const node = withStatus(prev.poiNodes.get(parentKey), 'error');
                        return { ...prev, poiNodes: new Map(prev.poiNodes).set(parentKey, node) };
                    });
                    showErrorToast(error);
                })
                .finally(() => {
                    inFlightRef.current.delete(requestKey);
                    controllersRef.current.delete(requestKey);
                });
        },
        [children.poiNodes, clientId, ctxKey, filterParams, search, sort],
    );

    // Publish the loaders for the auto-expansion chain. Their identity changes with the node state,
    // so the fetch handlers reach them through this ref instead of closing over them — depending on
    // them directly would re-run the root fetch on every child load.
    useEffect(() => {
        loadersRef.current = { loadDmaPage, loadPoiPage };
    });

    const toggleArea = useCallback(
        (area: NetworkTableAreaRow) => {
            const resource: ChildResource = { kind: 'dmas', area: areaSelectorOf(area) };
            const parentKey = parentKeyOf(ctxKey, resource);
            const willExpand = !children.expandedAreas.has(parentKey);

            setChildrenState((prev) => {
                const base = prev.ctxKey === ctxKey ? prev : emptyChildren(ctxKey);
                const expandedAreas = new Set(base.expandedAreas);
                if (willExpand) expandedAreas.add(parentKey);
                else expandedAreas.delete(parentKey);
                return { ...base, expandedAreas };
            });

            // Only the very first expand fetches; reopening reuses the loaded children,
            // and a failed load is retried explicitly from its retry row.
            if (willExpand && !children.dmaNodes.has(parentKey)) loadDmaPage(area);
        },
        [children.dmaNodes, children.expandedAreas, ctxKey, loadDmaPage],
    );

    const toggleDma = useCallback(
        (area: NetworkTableAreaRow, dma: NetworkTableDmaRow) => {
            const resource: ChildResource = { kind: 'pois', area: areaSelectorOf(area), dma: dmaSelectorOf(dma) };
            const parentKey = parentKeyOf(ctxKey, resource);
            const willExpand = !children.expandedDmas.has(parentKey);

            setChildrenState((prev) => {
                const base = prev.ctxKey === ctxKey ? prev : emptyChildren(ctxKey);
                const expandedDmas = new Set(base.expandedDmas);
                if (willExpand) expandedDmas.add(parentKey);
                else expandedDmas.delete(parentKey);
                return { ...base, expandedDmas };
            });

            if (willExpand && !children.poiNodes.has(parentKey)) loadPoiPage(area, dma);
        },
        [children.expandedDmas, children.poiNodes, ctxKey, loadPoiPage],
    );

    const rows = useMemo<NetworkTableRow[]>(() => {
        const flat: NetworkTableRow[] = [];
        const areas = activeData?.areas ?? [];

        for (const area of areas) {
            const areaResource: ChildResource = { kind: 'dmas', area: areaSelectorOf(area) };
            const areaParentKey = parentKeyOf(ctxKey, areaResource);
            const areaExpanded = children.expandedAreas.has(areaParentKey);
            flat.push({ kind: 'area', key: areaParentKey, area, expanded: areaExpanded });
            if (!areaExpanded) continue;

            const dmaNode = children.dmaNodes.get(areaParentKey);
            if (!dmaNode || (dmaNode.status === 'loading' && dmaNode.rows.length === 0)) {
                flat.push({ kind: 'status', key: `${areaParentKey}:status`, level: 'dma', state: 'loading', area });
                continue;
            }
            if (dmaNode.status === 'error' && dmaNode.rows.length === 0) {
                flat.push({ kind: 'status', key: `${areaParentKey}:status`, level: 'dma', state: 'error', area });
                continue;
            }
            if (dmaNode.rows.length === 0 && !dmaNode.hasNext) {
                flat.push({ kind: 'status', key: `${areaParentKey}:status`, level: 'dma', state: 'empty', area });
                continue;
            }

            for (const dma of dmaNode.rows) {
                const dmaResource: ChildResource = {
                    kind: 'pois',
                    area: areaSelectorOf(area),
                    dma: dmaSelectorOf(dma),
                };
                const dmaParentKey = parentKeyOf(ctxKey, dmaResource);
                const dmaExpanded = children.expandedDmas.has(dmaParentKey);
                flat.push({ kind: 'dma', key: dmaParentKey, area, dma, expanded: dmaExpanded });
                if (!dmaExpanded) continue;

                const poiNode = children.poiNodes.get(dmaParentKey);
                if (!poiNode || (poiNode.status === 'loading' && poiNode.rows.length === 0)) {
                    flat.push({
                        kind: 'status',
                        key: `${dmaParentKey}:status`,
                        level: 'poi',
                        state: 'loading',
                        area,
                        dma,
                    });
                    continue;
                }
                if (poiNode.status === 'error' && poiNode.rows.length === 0) {
                    flat.push({
                        kind: 'status',
                        key: `${dmaParentKey}:status`,
                        level: 'poi',
                        state: 'error',
                        area,
                        dma,
                    });
                    continue;
                }
                if (poiNode.rows.length === 0 && !poiNode.hasNext) {
                    flat.push({
                        kind: 'status',
                        key: `${dmaParentKey}:status`,
                        level: 'poi',
                        state: 'empty',
                        area,
                        dma,
                    });
                    continue;
                }

                for (const poi of poiNode.rows) {
                    flat.push({ kind: 'poi', key: `${dmaParentKey}|poi:${poi.id}`, poi, area, dma });
                }
                if (poiNode.hasNext) {
                    flat.push({
                        kind: 'loadMore',
                        key: `${dmaParentKey}:more`,
                        level: 'poi',
                        nextCount: nextBatchCount(poiNode),
                        parentLabel: dmaLabel(dma),
                        state: loadMoreState(poiNode),
                        area,
                        dma,
                    });
                }
            }

            if (dmaNode.hasNext) {
                flat.push({
                    kind: 'loadMore',
                    key: `${areaParentKey}:more`,
                    level: 'dma',
                    nextCount: nextBatchCount(dmaNode),
                    parentLabel: areaLabel(area),
                    state: loadMoreState(dmaNode),
                    area,
                });
            }
        }

        return flat;
    }, [activeData, children, ctxKey]);

    const status: 'idle' | 'loading' | 'loaded' | 'error' =
        rootKey === null ? 'idle' : errorKey === rootKey ? 'error' : loadedKey === rootKey ? 'loaded' : 'loading';

    return {
        rows,
        status,
        totalFilteredPoiCount: activeData?.totalFilteredPoiCount ?? 0,
        sort,
        applySort,
        retryRoot,
        toggleArea,
        toggleDma,
        loadMoreDmas: loadDmaPage,
        loadMorePois: loadPoiPage,
        retryDmas: loadDmaPage,
        retryPois: loadPoiPage,
    };
}
